import React from 'react';
import GuestLayout from '../../../layouts/GuestLayout';
import { Shield, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { useEffect } from 'react';

const TermsConditions = () => {

useEffect(() => {
  window.scrollTo(0, 0);
}, []);    
return (
    <GuestLayout>
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 animate-fadeIn">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12 animate-slideInDown">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-6 animate-scaleIn">
                        <FileText className="w-8 h-8 text-red-600" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-900 mb-4 animate-slideInUp">
                        Terms & Conditions
                    </h1>
                    <div className="mt-4 inline-flex items-center px-4 py-2 bg-red-50 rounded-full animate-slideInUp" style={{ animationDelay: '0.1s' }}>
                        <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                        <span className="text-sm font-medium text-red-600">
                            Please read these terms carefully before using our services
                        </span>
                    </div>
                </div>

                {/* Main Content */}
                <div className="bg-white rounded-2xl shadow-lg p-8 animate-slideInUp" style={{ animationDelay: '0.2s' }}>
                    {/* Introduction */}
                    <section className="mb-10">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-red-600 font-bold">1</span>
                            </div>
                            Introduction
                        </h2>
                        <div className="ml-11">
                            <p className="text-gray-700 mb-4">
                                Welcome to TXT Connect ("we," "our," or "us"). These Terms and Conditions govern your use of our bulk messaging platform, including our website, APIs, and related services. By accessing or using TXT Connect, you agree to be bound by these terms.
                            </p>
                            <p className="text-gray-700">
                                TXT Connect provides a platform for businesses and individuals to send bulk SMS messages, manage contacts, and analyze messaging campaigns. Our services are designed to be compliant with telecommunications regulations in Ghana and other operating regions.
                            </p>
                        </div>
                    </section>

                    {/* Account Terms */}
                    <section className="mb-10">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-red-600 font-bold">2</span>
                            </div>
                            Account Registration & Security
                        </h2>
                        <div className="ml-11 space-y-4">
                            <div className="flex items-start">
                                <CheckCircle className="w-5 h-5 text-red-600 mt-1 mr-3 flex-shrink-0" />
                                <p className="text-gray-700">
                                    You must be at least 18 years old to create an account and use our services.
                                </p>
                            </div>
                            <div className="flex items-start">
                                <CheckCircle className="w-5 h-5 text-red-600 mt-1 mr-3 flex-shrink-0" />
                                <p className="text-gray-700">
                                    You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
                                </p>
                            </div>
                            <div className="flex items-start">
                                <CheckCircle className="w-5 h-5 text-red-600 mt-1 mr-3 flex-shrink-0" />
                                <p className="text-gray-700">
                                    You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Acceptable Use */}
                    <section className="mb-10">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-red-600 font-bold">3</span>
                            </div>
                            Acceptable Use Policy
                        </h2>
                        <div className="ml-11">
                            <div className="bg-red-50 border-l-4 border-red-600 p-4 mb-6">
                                <p className="font-semibold text-red-800 mb-2">Prohibited Activities:</p>
                                <ul className="list-disc list-inside text-gray-700 space-y-2">
                                    <li>Sending spam, unsolicited commercial messages, or phishing attempts</li>
                                    <li>Transmitting unlawful, harassing, libelous, abusive, threatening, harmful, or obscene content</li>
                                    <li>Violating any local, state, national, or international laws or regulations</li>
                                    <li>Impersonating any person or entity or falsely stating your affiliation</li>
                                    <li>Using the service to send messages to recipients who have not opted in</li>
                                    <li>Attempting to gain unauthorized access to our systems or networks</li>
                                </ul>
                            </div>
                            <p className="text-gray-700">
                                We reserve the right to suspend or terminate accounts that violate our Acceptable Use Policy without refund.
                            </p>
                        </div>
                    </section>

                    {/* Service Usage */}
                    <section className="mb-10">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-red-600 font-bold">4</span>
                            </div>
                            Service Usage & Limitations
                        </h2>
                        <div className="ml-11 space-y-4">
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Message Delivery</h3>
                                <p className="text-gray-700">
                                    While we strive for 99.9% delivery rates, we cannot guarantee 100% delivery of all messages due to factors beyond our control, including network issues, recipient device problems, or carrier restrictions.
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">SMS Credits</h3>
                                <p className="text-gray-700">
                                    SMS credits are non-transferable and non-refundable. Unused credits expire according to your subscription plan terms. We reserve the right to modify credit expiration policies with 30 days' notice.
                                </p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-lg">
                                <h3 className="font-semibold text-gray-900 mb-2">Service Availability</h3>
                                <p className="text-gray-700">
                                    We aim to provide 24/7 service availability but may need to perform maintenance that could result in temporary service interruptions. We will provide advance notice for scheduled maintenance when possible.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Payments & Billing */}
                    <section className="mb-10">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-red-600 font-bold">5</span>
                            </div>
                            Payments & Billing
                        </h2>
                        <div className="ml-11 space-y-4">
                            <p className="text-gray-700">
                                All fees are quoted in Ghanaian Cedis (GH₵) unless otherwise specified. Payment must be made in advance for subscription plans and SMS credits.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-4 border border-red-200 rounded-lg">
                                    <h3 className="font-semibold text-red-600 mb-2">Subscription Plans</h3>
                                    <p className="text-sm text-gray-700">
                                        Monthly and annual subscriptions automatically renew unless cancelled at least 48 hours before the renewal date.
                                    </p>
                                </div>
                                <div className="p-4 border border-red-200 rounded-lg">
                                    <h3 className="font-semibold text-red-600 mb-2">Pay-As-You-Go</h3>
                                    <p className="text-sm text-gray-700">
                                        Bulk SMS credits are purchased upfront and used as you send messages. Credits do not expire on active accounts.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Privacy & Data */}
                    <section className="mb-10">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                                <Shield className="w-4 h-4 text-red-600" />
                            </div>
                            Privacy & Data Protection
                        </h2>
                        <div className="ml-11">
                            <p className="text-gray-700 mb-4">
                                We are committed to protecting your privacy and personal data. Our Privacy Policy explains how we collect, use, and protect your information. By using our services, you consent to our data practices as described in our Privacy Policy.
                            </p>
                            <p className="text-gray-700">
                                You are responsible for obtaining necessary consents from message recipients and complying with data protection laws when using our platform.
                            </p>
                        </div>
                    </section>

                    {/* Termination */}
                    <section className="mb-10">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                                <span className="text-red-600 font-bold">7</span>
                            </div>
                            Termination
                        </h2>
                        <div className="ml-11 space-y-4">
                            <p className="text-gray-700">
                                Either party may terminate these Terms at any time by providing written notice. Upon termination:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                                <li>Your right to use the service immediately ceases</li>
                                <li>You must pay any outstanding fees</li>
                                <li>We may delete your data after 30 days of termination</li>
                                <li>Refunds are subject to our refund policy</li>
                            </ul>
                        </div>
                    </section>
                </div>

                {/* Footer Note */}
                <div className="mt-8 text-center animate-slideInUp" style={{ animationDelay: '0.3s' }}>
                    <p className="text-sm text-gray-500">
                        By using TXT Connect, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions.
                    </p>
                </div>
            </div>
        </div>
        <style>{`
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            @keyframes slideInDown {
                from { opacity: 0; transform: translateY(-20px); }
                to { opacity: 1; transform: translateY(0); }
            }
            @keyframes slideInUp {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
            }
            @keyframes scaleIn {
                from { opacity: 0; transform: scale(0.8); }
                to { opacity: 1; transform: scale(1); }
            }
            .animate-fadeIn { animation: fadeIn 0.6s ease-out; }
            .animate-slideInDown { animation: slideInDown 0.6s ease-out; }
            .animate-slideInUp { animation: slideInUp 0.6s ease-out; }
            .animate-scaleIn { animation: scaleIn 0.6s ease-out; }
        `}</style>
    </GuestLayout>
);
};

export default TermsConditions;